import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Typography,
} from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { Save } from "@mui/icons-material";
import { ComponentHeader, BrandButton } from "components";
import {
  getContactSettings,
  createContactSettings,
  updateContactSettings,
} from "actions/contactSettings";
import { clearError } from "features/contactSettingsSlice";
import {
  WORKING_HOURS_DAYS,
  WORKING_HOURS_DAY_ALIASES,
  DEFAULT_WORKING_HOURS,
} from "constants/general";
import { ADMIN_TEXTS } from "constants/adminTexts";
import "./ContactUsContacts.scss";

const T = ADMIN_TEXTS.contactUs;

const normalizeDay = (day) => {
  if (!day) return WORKING_HOURS_DAYS[0];
  return WORKING_HOURS_DAY_ALIASES[day] || day;
};

const parseWorkingHours = (str) => {
  if (!str) return DEFAULT_WORKING_HOURS;
  const lines = str.split("\n").filter(Boolean);
  return lines.slice(0, DEFAULT_WORKING_HOURS.length).map((line, i) => {
    const parts = line.split(/\s{2,}|\t/);
    const dayPart = parts[0]?.trim() || "";
    const timePart = parts.slice(1).join(" ").trim() || "";

    const dayMatch = dayPart.match(/^(\w+)\s*[–-]\s*(\w+)$/);
    const fromDay = normalizeDay(dayMatch ? dayMatch[1] : dayPart);
    const toDay = normalizeDay(dayMatch ? dayMatch[2] : dayPart);

    const timeMatch = timePart.match(/^(.+?)\s*[–-]\s*(.+)$/);
    const startTime = timeMatch
      ? timeMatch[1].trim()
      : DEFAULT_WORKING_HOURS[i].startTime;
    const endTime = timeMatch
      ? timeMatch[2].trim()
      : DEFAULT_WORKING_HOURS[i].endTime;

    return { fromDay, toDay, startTime, endTime };
  });
};

const formatWorkingHours = (rows) =>
  rows
    .map(({ fromDay, toDay, startTime, endTime }) => {
      const dayPart = fromDay === toDay ? fromDay : `${fromDay} – ${toDay}`;
      return `${dayPart}     ${startTime} – ${endTime}`;
    })
    .join("\n");

const validationSchema = Yup.object({
  receiverEmail: Yup.string().email(T.emailInvalid).required(T.emailRequired),
  phoneNumber: Yup.string().required(T.phoneNumberRequired),
});

export const ContactUsContacts = () => {
  const dispatch = useDispatch();
  const { settings, loading, updateLoading, error, settingsExist } =
    useSelector((state) => state.contactSettings);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      receiverEmail: settings?.receiverEmail || "",
      phoneNumber: settings?.phoneNumber || "",
      workingHours: parseWorkingHours(settings?.workingHours),
    },
    validationSchema,
    onSubmit: (values) => {
      const payload = {
        receiverEmail: values.receiverEmail,
        phoneNumber: values.phoneNumber,
        workingHours: formatWorkingHours(values.workingHours),
      };
      dispatch(
        settingsExist
          ? updateContactSettings(payload)
          : createContactSettings(payload),
      );
    },
  });

  const handleHoursChange = (index, field, value) => {
    const updated = formik.values.workingHours.map((row, i) =>
      i === index ? { ...row, [field]: value } : row,
    );
    formik.setFieldValue("workingHours", updated);
  };

  useEffect(() => {
    dispatch(getContactSettings());
    return () => {
      dispatch(clearError());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <Box className="contact-us-contacts__loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box className="contact-us-contacts">
        <ComponentHeader title={T.contactsPageTitle} />
        {error && (
          <Alert
            severity="error"
            className="contact-us-contacts__alert"
            onClose={() => dispatch(clearError())}
          >
            {typeof error === "string" ? error : T.genericError}
          </Alert>
        )}
        <form onSubmit={formik.handleSubmit}>
          <Box className="contact-us-contacts__form">
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={T.receiverEmailLabel}
                  name="receiverEmail"
                  value={formik.values.receiverEmail}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.receiverEmail &&
                    Boolean(formik.errors.receiverEmail)
                  }
                  helperText={
                    formik.touched.receiverEmail && formik.errors.receiverEmail
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={T.phoneNumberLabel}
                  name="phoneNumber"
                  value={formik.values.phoneNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.phoneNumber &&
                    Boolean(formik.errors.phoneNumber)
                  }
                  helperText={
                    formik.touched.phoneNumber && formik.errors.phoneNumber
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  className="contact-us-contacts__hours-title"
                >
                  {T.workingHoursTitle}
                </Typography>
                {formik.values.workingHours.map((row, index) => (
                  <Box key={index} className="contact-us-contacts__hours-row">
                    <Box className="contact-us-contacts__hours-group">
                      <FormControl
                        className="contact-us-contacts__day-select"
                        size="small"
                      >
                        <InputLabel>{T.fromLabel}</InputLabel>
                        <Select
                          label={T.fromLabel}
                          value={row.fromDay}
                          onChange={(e) =>
                            handleHoursChange(index, "fromDay", e.target.value)
                          }
                        >
                          {WORKING_HOURS_DAYS.map((day) => (
                            <MenuItem key={day} value={day}>
                              {day}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Typography
                        variant="body2"
                        className="contact-us-contacts__separator"
                      >
                        –
                      </Typography>

                      <FormControl
                        className="contact-us-contacts__day-select"
                        size="small"
                      >
                        <InputLabel>{T.toLabel}</InputLabel>
                        <Select
                          label={T.toLabel}
                          value={row.toDay}
                          onChange={(e) =>
                            handleHoursChange(index, "toDay", e.target.value)
                          }
                        >
                          {WORKING_HOURS_DAYS.map((day) => (
                            <MenuItem key={day} value={day}>
                              {day}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    <Box className="contact-us-contacts__hours-group">
                      <TimePicker
                        label={T.opensLabel}
                        value={dayjs(row.startTime, "h:mm A")}
                        onChange={(val) =>
                          handleHoursChange(
                            index,
                            "startTime",
                            val ? val.format("h:mm A") : row.startTime,
                          )
                        }
                        slotProps={{
                          textField: {
                            size: "small",
                            className: "contact-us-contacts__time-select",
                          },
                        }}
                      />

                      <Typography
                        variant="body2"
                        className="contact-us-contacts__separator"
                      >
                        –
                      </Typography>

                      <TimePicker
                        label={T.closesLabel}
                        value={dayjs(row.endTime, "h:mm A")}
                        onChange={(val) =>
                          handleHoursChange(
                            index,
                            "endTime",
                            val ? val.format("h:mm A") : row.endTime,
                          )
                        }
                        slotProps={{
                          textField: {
                            size: "small",
                            className: "contact-us-contacts__time-select",
                          },
                        }}
                      />
                    </Box>
                  </Box>
                ))}
                <Typography
                  variant="caption"
                  className="contact-us-contacts__hours-preview"
                >
                  {T.hoursPreviewLabel}{" "}
                  {formatWorkingHours(formik.values.workingHours).replaceAll(
                    "\n",
                    "  |  ",
                  )}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <BrandButton
                  type="submit"
                  variant="primary"
                  startIcon={<Save />}
                  disabled={updateLoading}
                >
                  {updateLoading ? T.saving : T.save}
                </BrandButton>
              </Grid>
            </Grid>
          </Box>
        </form>
      </Box>
    </LocalizationProvider>
  );
};
